import React from 'react';
import { MarketingPage, MarketingContent } from '../components/MarketingPage';

const content: MarketingContent = {
  seo: {
    title: 'File Sharing Without the Cloud — Private, Direct Transfers',
    description:
      'Send files straight between your own devices with nothing uploaded, stored, or scanned by anyone else. Private by design, across Windows, Mac, Linux, iPhone, and Android.',
    path: '/file-sharing-without-the-cloud',
  },
  eyebrow: 'Private by design',
  h1: <>File sharing <span className="em">without the cloud.</span></>,
  lead:
    'Your files move straight from one of your devices to another. Nothing is uploaded to a third-party drive, nothing is stored on someone else’s servers, and nothing is scanned along the way.',
  sections: [
    {
      h2: 'The cloud is just someone else’s computer',
      paras: [
        'Every time you drop a file into a big-tech drive to move it somewhere, you hand a copy to a company you don’t control. It sits on their servers, subject to their scanning, their outages, their data-retention rules, and their breaches — all to get a file from your laptop to your phone in the next room.',
        'Local Area Cloud takes the cloud out of the middle. The file goes directly between your devices.',
      ],
    },
    {
      h2: 'How your files stay off our servers',
      paras: [
        'Local Area Cloud’s cloud service is only a registry. It records which devices belong to your account and how to reach them on your network — nothing more. When you send a file, it travels directly from one device to the other over your local network. We never receive, store, or scan the contents of your files.',
      ],
      bullets: [
        <><b>What we hold:</b> your email, your device names, and basic connection details.</>,
        <><b>What we never see:</b> the files themselves — their contents never reach us.</>,
        <><b>Encryption keys stay local:</b> for encrypted features, private keys are generated and kept on your device.</>,
      ],
    },
    {
      h2: 'Private doesn’t have to mean local-only',
      paras: [
        'Sometimes you’re away from home and still want to reach your own devices. Local Area Cloud’s Pro tier lets you do that over your own VPN — the connection is yours end to end, and files still travel directly between your devices rather than through our servers. Privacy holds even when the distance grows.',
      ],
    },
    {
      h2: 'You keep the copy',
      paras: [
        'Because nothing is uploaded, there’s no second copy of your data living in an account you’ll forget about. Delete a file on your device and it’s gone — there’s no server-side ghost. It’s file sharing that behaves the way you’d expect: your files, on your devices, and nowhere else.',
      ],
    },
  ],
  faqs: [
    {
      q: 'Where do my files actually go?',
      a: 'Straight from one of your devices to another over your local network. They are not uploaded to us or to any third-party cloud storage.',
    },
    {
      q: 'What information does Local Area Cloud store about me?',
      a: 'Only what’s needed to sign you in and help your devices find each other: your email, device names, and basic connection details. Never your file contents.',
    },
    {
      q: 'Can you read my files?',
      a: 'No. File contents never reach our servers, so there is nothing on our side to read or scan.',
    },
    {
      q: 'What about sharing when I’m away from home?',
      a: 'Pro lets you reach your devices across networks over your own VPN. The connection stays yours and files still transfer directly between your devices.',
    },
    {
      q: 'Is it free?',
      a: 'Yes — free for up to two devices with all features. Pro adds unlimited devices and cross-network reach.',
    },
  ],
  related: [
    { label: 'Local network file sharing', to: '/local-network-file-sharing', note: 'How LAN transfer works' },
    { label: 'AirDrop for Windows', to: '/airdrop-for-windows', note: 'Cross-platform instant send' },
    { label: 'Transfer files between phone and PC', to: '/transfer-files-phone-to-pc', note: 'The phone ⇄ PC job' },
  ],
  ctaBand: {
    h2: 'Keep your files where they belong — with you.',
    p: 'Direct, private transfers between your own devices. Nothing uploaded. Free for up to two.',
  },
};

export const FileSharingWithoutCloud: React.FC = () => <MarketingPage content={content} />;
export default FileSharingWithoutCloud;
